import { cva } from "class-variance-authority";
import Image from "next/image";
import { type ElementType, forwardRef, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Typography } from "@/components/ui/Typography";

/**
 * Shared product-variant swatch atom (Figma: `product-variant-selector`).
 *
 * Renders the colour circle, an optional text label, and the selected
 * ("active") treatment. Defaults to a `<button>`; pass the i18n `Link` via
 * `as` for navigation (the PDP picks variants by URL). Event handlers and
 * `aria-*` flow through to the rendered element, so callers own semantics.
 *
 * Circle size follows the Figma variants at the "Large" size: 24px when there
 * is no label ("Only Circle") and 14px when a label is present ("With Text").
 */

const containerClass = [
	"group inline-flex items-center gap-grid-component-spacing-2xs",
	"min-h-10 p-grid-component-spacing-2xs",
	"rounded-radius-component-specific-button",
	"transition-colors duration-200",
	"cursor-pointer disabled:cursor-default",
	"focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current",
].join(" ");

const circleVariants = cva(
	"relative shrink-0 overflow-hidden rounded-full border bg-background-bg-image transition-colors duration-200",
	{
		variants: {
			withLabel: { true: "size-3.5", false: "size-6" },
			active: {
				// Border darkens on hover (fade-1 → fade-2) and goes solid dark when selected.
				true: "border-border-dark",
				false: "border-fade-1 group-hover:border-fade-2",
			},
		},
		defaultVariants: { withLabel: false, active: false },
	},
);

interface SwatchOwnProps {
	/** Solid colour fill for the swatch. Takes precedence over `image`. */
	hex?: string;
	/** Image shown inside the swatch when no `hex` is set (variant thumbnail). */
	image?: { href: string; alt?: string };
	/** Optional text label — present renders the "With Text" (14px circle) variant. */
	label?: ReactNode;
	/** Selected state: dark ring on the circle and an underline under the label. */
	active?: boolean;
	/** Element to render. Defaults to `<button>`; pass the i18n `Link` for navigation. */
	as?: ElementType;
}

export type SwatchProps = SwatchOwnProps &
	Omit<React.AllHTMLAttributes<HTMLElement>, keyof SwatchOwnProps | "color">;

export const Swatch = forwardRef<HTMLElement, SwatchProps>(function Swatch(
	{ hex, image, label, active = false, as, className, type, ...rest },
	ref,
) {
	const Component: ElementType = as ?? "button";
	const hasLabel = label != null && label !== "";

	return (
		<Component
			ref={ref}
			// Figma: the hover background only appears on the "With Text" variant.
			className={twMerge(containerClass, hasLabel && "hover:bg-fade-1", className)}
			// `type` is only meaningful on a native <button>; default it there and
			// drop it for polymorphic elements (e.g. an anchor) that don't accept it.
			type={as == null ? (type ?? "button") : type}
			{...rest}
		>
			<span
				className={circleVariants({ withLabel: hasLabel, active })}
				style={hex ? { backgroundColor: hex } : undefined}
			>
				{!hex && image && (
					<Image
						src={image.href}
						alt={image.alt ?? ""}
						fill
						sizes={hasLabel ? "14px" : "24px"}
						className="object-cover"
					/>
				)}
			</span>
			{hasLabel && (
				<span className={twMerge("border-b", active ? "border-border-dark" : "border-transparent")}>
					<Typography variant="body-sm-reg" as="span" className="text-text-dark-text-1 capitalize">
						{label}
					</Typography>
				</span>
			)}
		</Component>
	);
});

Swatch.displayName = "Swatch";
